import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Button,
  TextField,
  InputAdornment,
  Tooltip,
  Select,
  MenuItem
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import WrenchIcon from "@material-ui/icons/Build";

import {
  clearSelectedElement,
  selectProcess,
  selectSubmission
} from "../actions/selectActions";
import { openReferenceModal, openApprovalModal } from "../actions/modalActions";
import {
  extendList,
  setSearchTerm,
  setSearchMode
} from "../actions/processActions";
import "../styles/processList.scss";
import periodicTable from "../PeriodicTable";

const elementDescriptors = {};

for (let element in periodicTable.elements) {
  const { symbol, name } = periodicTable.elements[element];
  elementDescriptors[symbol] = name;
}

class ProcessList extends Component {
  constructor(props) {
    super(props);
    this.state = { referenceModalIsOpen: false };
  }

  changeSearchTerm = event => {
    this.props.setSearchTerm(event.target.value);
  };

  getTitle = () => {
    if (this.props.processesLoading) {
      return "Loading list of processes...";
    }

    if (this.props.selectedElement) {
      return (
        <>
          {"List of processes using " +
            elementDescriptors[this.props.selectedElement].toLowerCase()}
        </>
      );
    }

    return "List of processes";
  };

  componentDidMount() {
    window.addEventListener("scroll", this.onScroll, false);
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.onScroll, false);
  }

  onScroll = () => {
    if (
      window.innerHeight + window.scrollY >=
      document.body.offsetHeight - 200
    ) {
      this.props.extendList();
    }
  };

  clearFilters = () => {
    this.props.clearSelectedElement();
    this.props.setSearchTerm("");
    this.props.setSearchMode("all");
  };

  createList = maxLength => {
    let length = 0;

    let elements;

    if (this.props.selectedElement) {
      elements = periodicTable.elements.concat().sort((a, b) => {
        if (a.symbol === this.props.selectedElement) return -1;
        if (b.symbol === this.props.selectedElement) return 1;

        return a.number - b.number;
      });
    } else {
      elements = periodicTable.elements;
    }

    return elements.map(element => {
      const elementProcesses = this.props.processes
        .filter(
          process =>
            process.elements.includes(element.symbol) &&
            !element.symbol.match(/^(H|Te|F|O|N|S|Se)$/)
        )
        .filter(process => {
          const searchTerm = this.props.searchTerm.toLowerCase();
          const searchMode = this.props.searchMode;
          const selectedElement = this.props.selectedElement;

          if (!searchTerm && !selectedElement) return true;

          if (selectedElement && process.elements.includes(selectedElement))
            return true;

          if (!searchTerm) return false;

          if (
            (searchMode === "material" || searchMode === "all") &&
            process.material.toLowerCase().includes(searchTerm)
          )
            return true;

          if (
            (searchMode === "reactant" || searchMode === "all") &&
            process.reactants.some(reactant =>
              reactant.toLowerCase().includes(searchTerm)
            )
          )
            return true;

          if (
            (searchMode === "reference" || searchMode === "all") &&
            process.references.some(
              reference =>
                reference.author.toLowerCase().includes(searchTerm) ||
                reference.created.includes(searchTerm)
            )
          )
            return true;

          return false;
        })
        .sort((a, b) => {
          if (a.elements.length !== b.elements.length) {
            return a.elements.length - b.elements.length;
          }

          if (a.material === b.material) {
            return a.reactants[0].localeCompare(b.reactants[0]);
          }

          return a.material.localeCompare(b.material);
        });

      if (!elementProcesses.length) return <></>;

      if (length >= maxLength) return <></>;

      return (
        <>
          <tr className="processList--subtitle">
            <td>{element.number}</td>
            <td>{element.name}</td>
            <td />
            <td />
            <td />
            <td />
            <td />
          </tr>
          {elementProcesses.map((process, index, array) => {
            if (length >= maxLength) return <></>;
            length++;
            return (
              <Tooltip title={process.note}>
                <tr>
                  <td>
                    {this.props.adminPassword && (
                      <WrenchIcon
                        onClick={() => {
                          this.props.openApprovalModal();
                          this.props.selectSubmission(
                            { ...process, reference: process.references[0] },
                            "process"
                          );
                        }}
                      />
                    )}
                  </td>
                  <td>
                    {(index === 0 ||
                      array[index - 1].material !== process.material) &&
                      process.materialSubscripts}
                  </td>
                  {[0, 1, 2, 3].map(index => (
                    <td>{process.reactantsSubscripts[index]}</td>
                  ))}
                  <td>
                    {process.references.slice(0, 3).map(reference => (
                      <a
                        className="processList--link"
                        style={{ marginLeft: "0.2em" }}
                        href={"https://doi.org/" + reference.doi}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {reference.author}
                        <i> et al.</i>
                      </a>
                    ))}
                    {process.references.length > 3 &&
                      ` and ${process.references.length - 3} more`}
                    <Button
                      color="primary"
                      onClick={() => {
                        this.props.selectProcess(process);
                        this.props.openReferenceModal();
                      }}
                    >
                      {process.references.length > 3 ? "View All" : "Add"}
                    </Button>
                  </td>
                </tr>
              </Tooltip>
            );
          })}
        </>
      );
    });
  };

  getListContent = () => {
    if (this.props.processesLoading) return <div />;

    return (
      <table className="processList--table">
        <tbody>
          <tr className="header">
            <td>Z</td>
            <td>Material</td>
            <td>Reactant A</td>
            <td>Reactant B</td>
            <td>Reactant C</td>
            <td>Further reactants</td>
            <td>References</td>
          </tr>
          {this.createList(this.props.listLength)}
        </tbody>
      </table>
    );
  };

  render() {
    return (
      <div className="processList">
        <div className="processList--title">
          <div>{this.getTitle()}</div>
          {this.props.selectedElement ||
          this.props.searchMode !== "all" ||
          this.props.searchTerm ? (
            <div style={{ position: "relative" }}>
              <Button
                variant="contained"
                color="primary"
                className="center-vertical"
                style={{ fontSize: "33%" }}
                onClick={this.clearFilters}
              >
                Clear
              </Button>
            </div>
          ) : (
            <div />
          )}
          <div>
            <TextField
              className="center-vertical"
              id="input-with-icon-textfield"
              placeholder="Search"
              onChange={this.changeSearchTerm}
              style={{ width: "100%" }}
              value={this.props.searchTerm}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </div>
          <div>
            <Select
              value={this.props.searchMode}
              className="center-vertical"
              style={{ width: "100%", textAlign: "left" }}
              onChange={e => this.props.setSearchMode(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="material">Material</MenuItem>
              <MenuItem value="reactant">Reactants</MenuItem>
              <MenuItem value="reference">References</MenuItem>
            </Select>
          </div>
        </div>
        {this.getListContent()}
      </div>
    );
  }
}
function mapStateToProps(state) {
  return {
    processesLoading: state.process.loading,
    processes: state.process.processes,
    selectedElement: state.selected.element,
    listLength: state.process.listLength,
    searchTerm: state.process.searchTerm,
    searchMode: state.process.searchMode,
    adminPassword: state.admin.password
  };
}
export default connect(
  mapStateToProps,
  {
    selectSubmission,
    clearSelectedElement,
    selectProcess,
    openReferenceModal,
    openApprovalModal,
    extendList,
    setSearchTerm,
    setSearchMode
  }
)(ProcessList);
